import { Controller } from "@hotwired/stimulus"
import { debug } from '../utils/debug'
import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import XHRUpload from '@uppy/xhr-upload'
import AwsS3 from '@uppy/aws-s3'

// Uppy CSS is bundled via Webpack in the CRM entry; no dynamic loading here
const uppyByElement = new WeakMap()
const disconnectTimerByElement = new WeakMap()

// Connects to data-controller="uppy-uploader"
export default class extends Controller {
  static values = {
    endpoint: String,
    presignedUrlEndpoint: String,
    uploadCompleteEndpoint: String,
    maxFiles: Number,
    maxFileSize: Number,
    allowedFileTypes: String,
    fieldName: String,
    hiddenFieldName: String,
    autoProceed: Boolean,
    height: Number,
    note: String,
    resourceType: String,
    resourceId: String,
    category: String,
    manualSubmit: Boolean,
    ui: String, // 'dashboard' (default) or 'drag-drop'
    mode: String // 'xhr' (default) or 's3'
  }

  static targets = ["dashboard", "hiddenField", "submitButton", "messages", "previews"]

  connect() {
    debug('uppy-uploader', 'Uppy controller connecting...')

    // Cancel any pending disconnect cleanup for this element
    const pending = disconnectTimerByElement.get(this.element)
    if (pending) {
      clearTimeout(pending)
      disconnectTimerByElement.delete(this.element)
    }

    // Reuse existing instance if present
    const existing = uppyByElement.get(this.element)
    if (existing) {
      this.uppy = existing
      return
    }

    // Prevent multiple initializations
    if (this.uppy) {
      debug('uppy-uploader', 'Uppy already exists, skipping initialization')
      return
    }

    // Clear any existing timeout
    if (this.initTimeout) {
      clearTimeout(this.initTimeout)
      this.initTimeout = null
    }

    // Add a small delay to prevent rapid re-initialization during early DOM churn
    this.initTimeout = setTimeout(() => {
      this.initializeUppy()
    }, 200)
  }

  disconnect() {
    // Clear any pending initialization
    if (this.initTimeout) {
      clearTimeout(this.initTimeout)
      this.initTimeout = null
    }

    // Debounce destroy to avoid thrashing during rapid DOM mutations
    const timer = setTimeout(() => {
      // If the element has been reattached, skip destroying
      if (this.element && this.element.isConnected) {
        disconnectTimerByElement.delete(this.element)
        return
      }

      const instance = uppyByElement.get(this.element)
      if (instance) {
        debug('uppy-uploader', 'Uppy controller disconnecting... destroying instance')
        instance.destroy()
        uppyByElement.delete(this.element)
      }
      this.uppy = null
      disconnectTimerByElement.delete(this.element)
    }, 500)
    disconnectTimerByElement.set(this.element, timer)
  }

  initializeUppy() {
    // Ensure dashboard target exists
    if (!this.hasDashboardTarget) {
      console.error('Uppy uploader: dashboard target not found')
      return
    }

    // Check if dashboard is already initialized
    if (this.dashboardTarget.hasChildNodes()) {
      debug('uppy-uploader', '📸📽️ Dashboard target already has content, skipping Uppy initialization')
      return
    }

    debug('uppy-uploader', 'Dashboard target found:', this.dashboardTarget)

    // Default values
    const maxFiles = this.maxFilesValue || 10
    const maxFileSize = this.maxFileSizeValue || 10 * 1024 * 1024 // 10MB
    let allowedFileTypes = ['image/*']
    if (this.hasAllowedFileTypesValue) {
      const rawValue = String(this.allowedFileTypesValue || '').trim()
      if (rawValue) {
        // Expect a simple comma-separated list from the view layer
        allowedFileTypes = rawValue.split(',').map(s => s.trim()).filter(Boolean)
      }
    }
    const fieldName = this.fieldNameValue || 'file'
    const height = this.heightValue || 400
    const note = this.noteValue || 'Image files only, up to 10MB each'
    const autoProceed = this.hasAutoProceedValue ? this.autoProceedValue : false

    this.uppy = new Uppy({
      debug: (window && window.env === 'development'),
      autoProceed: autoProceed,
      restrictions: {
        maxNumberOfFiles: maxFiles,
        maxFileSize: maxFileSize,
        allowedFileTypes: allowedFileTypes
      }
    })

    // Always use Dashboard UI now
      this.uppy.use(Dashboard, {
        inline: true,
        target: this.dashboardTarget,
        replaceTargetContent: true,
        showProgressDetails: true,
        hideProgressAfterFinish: false,
        theme: 'light',
        width: '100%',
        height: height,
        proudlyDisplayPoweredByUppy: false,
        note: note,
        hideUploadButton: true,
        hideRetryButton: true,
        hidePauseResumeButton: true,
        hideCancelButton: true,
        // Consolidate drag/drop messaging to avoid duplicate lines
        locale: {
          strings: {
            // Hide the extra top hint to avoid duplicate drag/drop messaging
            dropHint: '',
            browseFiles: 'browse files',
            dropPaste: 'Drag & drop or %{browseFiles}',
            dropPasteFiles: 'Drag & drop or %{browseFiles}',
            dropPasteFolders: 'Drag & drop or %{browseFolders}',
            dropPasteBoth: 'Drag & drop or %{browseFiles}'
          }
        },
        browserBackButtonClose: true,
        showRemoveButtonAfterComplete: true
      })
    // Configure upload based on mode
    const mode = this.modeValue || 'xhr'

    if (mode === 's3') {
      // Configure AWS S3 upload
      this.uppy.use(AwsS3, {
        getUploadParameters: (file) => this.getUploadParameters(file),
        shouldUseMultipart: false // Use single PUT request for better compatibility
      })

      // Handle S3 upload success
      this.uppy.on('upload-success', (file, response) => {
        debug('uppy-uploader', 'S3 upload successful:', file.name, response)
        this.handleS3UploadSuccess(file, response)
      })

      // Handle S3 upload errors
      this.uppy.on('upload-error', (file, error, response) => {
        console.error('S3 upload error:', file.name, error, response)
        this.handleUploadError(file, error, response)
      })
    } else {
      // Configure XHR upload (default)
      this.uppy.use(XHRUpload, {
        endpoint: this.endpointValue,
        fieldName: fieldName,
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        formData: true,
        withCredentials: true,
        getResponseData: (responseText, response) => {
          try {
            const actualResponseText = responseText.responseText || responseText
            const parsed = JSON.parse(actualResponseText)
            debug('uppy-uploader', '📸📽️ Upload response:', parsed)
            return parsed
          } catch (e) {
            console.error('XHRUpload parse error:', e)
            return { message: 'Upload failed', files_list: '[]' }
          }
        },
        getResponseError: (xhr, response) => {
          return new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`)
        }
      })
    }
    // If not autoProceed, upload on submit; if autoProceed, accumulate ids on success
    const form = this.element.querySelector('form') || this.element.closest('form')
    if (!autoProceed && form) {
      form.addEventListener('submit', (e) => {
        // Avoid re-entrancy when re-dispatching submit
        if (form.dataset.uppySubmitted === 'true') return
        if (this.uppy.getFiles().length === 0) return
        e.preventDefault()
        this.uppy.upload().then((result) => {
          if (result.failed && result.failed.length > 0) {
            this.showMessage('Upload failed. Please try again.', 'error')
            return
          }
          const allIds = []
          result.successful.forEach((file) => {
            const body = (file.response && file.response.body) || {}
            let ids = []
            if (body && body.files_list) {
              try {
                const parsed = typeof body.files_list === 'string' ? JSON.parse(body.files_list) : body.files_list
                ids = Array.isArray(parsed) ? parsed : []
              } catch (e) {
                console.error('Failed parsing files_list:', e)
              }
            }
            if (body && Array.isArray(body.upload_ids)) {
              ids = ids.concat(body.upload_ids)
            }
            ids.forEach((id) => allIds.push(id))
          })
          if (this.hasHiddenFieldTarget) {
            this.hiddenFieldTarget.value = JSON.stringify(allIds)
          }
          // Mark as submitted and re-dispatch submit so validators/Turnstile run
          form.dataset.uppySubmitted = 'true'
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit()
          } else {
            form.submit()
          }
        })
      })
    } else if (autoProceed) {
      this.uppy.on('upload-success', (_file, response) => {
        debug('uppy-uploader', '📸📽️ Upload success event:', response)
        const body = (response && response.body) || {}
        debug('uppy-uploader', '📸📽️ Response body:', body)
        let ids = []
        if (body && body.files_list) {
          try {
            const parsed = typeof body.files_list === 'string' ? JSON.parse(body.files_list) : body.files_list
            ids = Array.isArray(parsed) ? parsed : []
            debug('uppy-uploader', '📸📽️ Parsed file IDs from files_list:', ids)
          } catch (e) {
            console.error('Failed parsing files_list:', e)
          }
        }
        if (body && Array.isArray(body.upload_ids)) {
          ids = ids.concat(body.upload_ids)
          debug('uppy-uploader', '📸📽️ Added upload_ids:', body.upload_ids)
        }
        if (this.hasHiddenFieldTarget && ids.length > 0) {
          const current = this.hiddenFieldTarget.value ? JSON.parse(this.hiddenFieldTarget.value) : []
          const next = [...current, ...ids]
          this.hiddenFieldTarget.value = JSON.stringify(next)
          debug('uppy-uploader', '📸📽️ Updated hidden field to:', this.hiddenFieldTarget.value)
        } else {
          debug('uppy-uploader', '📸📽️ No hidden field target or no IDs to add. Has target:', this.hasHiddenFieldTarget, 'IDs:', ids)
        }
      })

      // If user submits while uploads are in progress, wait for completion then submit
      if (form) {
        form.addEventListener('submit', (e) => {
          debug('uppy-uploader', '📸📽️ Form submit event triggered')
          if (form.dataset.uppySubmitted === 'true') {
            debug('uppy-uploader', '📸📽️ Form already marked as submitted, allowing normal submission')
            return
          }
          const files = this.uppy.getFiles()
          debug('uppy-uploader', '📸📽️ Files in Uppy:', files.map(f => ({ name: f.name, status: f.status })))
          const hasPending = files.some((f) => ['uploading', 'preprocessing', 'postprocessing'].includes(f.status))
          const hasQueued = files.some((f) => f.status === 'queued')
          debug('uppy-uploader', '📸📽️ Has pending:', hasPending, 'Has queued:', hasQueued)
          if (hasPending || hasQueued) {
            e.preventDefault()
            this.showMessage('Please wait while your files finish uploading…', 'info')
            this.uppy.upload().then((result) => {
              if (result.failed && result.failed.length > 0) {
                this.showMessage('Some files failed to upload. Please review before submitting.', 'error')
                return
              }
              // Ensure hidden field includes all IDs
              const allIds = []
              result.successful.forEach((file) => {
                const body = (file.response && file.response.body) || {}
                let ids = []
                if (body && body.files_list) {
                  try {
                    const parsed = typeof body.files_list === 'string' ? JSON.parse(body.files_list) : body.files_list
                    ids = Array.isArray(parsed) ? parsed : []
                  } catch (e) {
                    console.error('Failed parsing files_list:', e)
                  }
                }
                if (body && Array.isArray(body.upload_ids)) {
                  ids = ids.concat(body.upload_ids)
                }
                ids.forEach((id) => allIds.push(id))
              })
              if (this.hasHiddenFieldTarget && allIds.length > 0) {
                const current = this.hiddenFieldTarget.value ? JSON.parse(this.hiddenFieldTarget.value) : []
                const merged = Array.from(new Set([...current, ...allIds]))
                this.hiddenFieldTarget.value = JSON.stringify(merged)
              }
              form.dataset.uppySubmitted = 'true'
              if (typeof form.requestSubmit === 'function') {
                form.requestSubmit()
              } else {
                form.submit()
              }
            })
          }
        })
      }
    }

    // Remember instance per element
    uppyByElement.set(this.element, this.uppy)

    this.setupEventListeners()
  }

  setupEventListeners() {
    // Handle successful uploads
    this.uppy.on('complete', (result) => {
      debug('uppy-uploader', '📸📽️ Upload complete!', result)
      if (result.successful.length > 0) {
        this.showMessage(`Successfully uploaded ${result.successful.length} file(s)`, 'success')

        // Extract file IDs from successful uploads and set the hidden field
        const fileIds = result.successful.map(file => {
          const response = file.response
          const responseData = response.body || response
          debug('uppy-uploader', '📸📽️ File response data:', responseData)

          if (responseData.files_list) {
            try {
              const parsed = JSON.parse(responseData.files_list)
              return Array.isArray(parsed) ? parsed : [parsed]
            } catch (e) {
              console.error('Error parsing files_list:', e)
              return []
            }
          }
          return []
        }).flat()

        debug('uppy-uploader', '📸📽️ Extracted file IDs:', fileIds)

        // Set the hidden field with the file IDs
        if (this.hasHiddenFieldTarget) {
          this.hiddenFieldTarget.value = JSON.stringify(fileIds)
          debug('uppy-uploader', '📸📽️ Set hidden field value to:', this.hiddenFieldTarget.value)
        }

        // Auto-submit the form if manual_submit is false AND auto_proceed is false
        // If auto_proceed is true, files upload immediately and user fills form after
        // If auto_proceed is false, files upload on form submit, so we auto-submit after upload
        const autoProceed = this.hasAutoProceedValue ? this.autoProceedValue : false
        if (!this.manualSubmitValue && !autoProceed) {
          debug('uppy-uploader', '📸📽️ Auto-submitting form...')
          this.submitForm()
        }
      }
    })

    // Handle upload errors
    this.uppy.on('error', (error) => {
      console.error('📸📽️ Upload error:', error)
      this.showMessage(`Upload failed: ${error.message || 'Unknown error'}`, 'error')
    })

    // Handle individual file errors
    this.uppy.on('upload-error', (file, error, response) => {
      console.error('📸📽️ File upload error:', file.name, error, response)
      this.showMessage(`Failed to upload ${file.name}: ${error.message || 'Unknown error'}`, 'error')
    })
  }


  showMessage(message, type = 'info') {
    if (this.hasMessagesTarget) {
      const messageDiv = document.createElement('div')
      messageDiv.className = `alert alert-${type === 'error' ? 'danger' : type} mt-2`
      messageDiv.textContent = message

      this.messagesTarget.appendChild(messageDiv)

      // Auto-hide success messages after 5 seconds
      if (type === 'success') {
        setTimeout(() => {
          messageDiv.remove()
        }, 5000)
      }
    }
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }

  getAdditionalFormData() {
    const formData = {}
    if (this.hasResourceTypeValue) {
      formData.resource_type = this.resourceTypeValue
    }
    if (this.hasResourceIdValue) {
      formData.resource_id = this.resourceIdValue
    }
    if (this.hasCategoryValue) {
      formData.category = this.categoryValue
    }
    return formData
  }

  // S3-specific methods
  async getUploadParameters(file) {
    try {
      // Build query parameters for GET request
      const params = new URLSearchParams({
        file_name: file.name,
        content_type: file.type,
        file_size: file.size,
        category: this.categoryValue || 'photo'
      })

      if (this.resourceTypeValue) {
        params.append('resource_type', this.resourceTypeValue)
      }
      if (this.resourceIdValue) {
        params.append('resource_id', this.resourceIdValue)
      }

      const response = await fetch(`${this.presignedUrlEndpointValue}?${params}`, {
        method: 'GET',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to get presigned URL: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      debug('uppy-uploader', 'Presigned URL data:', data)

      return {
        method: 'PUT',
        url: data.presigned_url,
        fields: {},
        headers: {
          'Content-Type': file.type
        }
      }
    } catch (error) {
      console.error('Error getting presigned URL:', error)
      throw error
    }
  }

  async handleS3UploadSuccess(file, response) {
    try {
      // Notify backend about successful upload
      const uploadResponse = await fetch(this.uploadCompleteEndpointValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          key: this.extractS3KeyFromUrl(response.uploadURL), // Extract full S3 key from URL
          file_name: file.name,
          content_type: file.type,
          file_size: file.size,
          category: this.categoryValue || 'photo',
          resource_type: this.resourceTypeValue,
          resource_id: this.resourceIdValue
        })
      })

      if (!uploadResponse.ok) {
        throw new Error(`Failed to complete upload: ${uploadResponse.status} ${uploadResponse.statusText}`)
      }

      const uploadData = await uploadResponse.json()
      debug('uppy-uploader', 'Upload completion response:', uploadData)

      // Store upload ID in hidden field
      if (this.hasHiddenFieldTarget) {
        const currentIds = this.hiddenFieldTarget.value ? JSON.parse(this.hiddenFieldTarget.value) : []
        const newIds = [...currentIds, ...uploadData.upload_ids]
        this.hiddenFieldTarget.value = JSON.stringify(newIds)
      }

      // Show success message
      this.showMessage(`File "${file.name}" uploaded successfully!`, 'success')

    } catch (error) {
      console.error('Error completing upload:', error)
      this.showMessage(`Error completing upload for "${file.name}": ${error.message}`, 'error')
    }
  }

  handleUploadError(file, error, response) {
    console.error('Upload error:', file.name, error, response)
    this.showMessage(`Upload failed for "${file.name}": ${error.message}`, 'error')
  }

  showMessage(message, type = 'info') {
    if (this.hasMessagesTarget) {
      const messageEl = document.createElement('div')
      messageEl.className = `alert alert-${type === 'error' ? 'danger' : type}`
      messageEl.textContent = message
      this.messagesTarget.appendChild(messageEl)

      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (messageEl.parentNode) {
          messageEl.parentNode.removeChild(messageEl)
        }
      }, 5000)
    } else {
      debug('uppy-uploader', `[${type.toUpperCase()}] ${message}`)
    }
  }

  // Action methods
  clearUploads() {
    if (this.uppy) {
      this.uppy.reset()
      if (this.hasHiddenFieldTarget) {
        this.hiddenFieldTarget.value = ''
      }
    }
  }

  extractS3KeyFromUrl(url) {
    // Extract the S3 key from the full S3 URL
    // URL format: https://s3.us-central-1.wasabisys.com/bucket-name/path/to/file.ext?params
    try {
      const urlObj = new URL(url)
      // Remove the leading slash and get the pathname
      const fullPath = urlObj.pathname.substring(1) // Remove leading slash
      // Split by '/' and remove the first part (bucket name)
      const pathParts = fullPath.split('/')
      if (pathParts.length > 1) {
        return pathParts.slice(1).join('/') // Remove bucket name, keep the rest
      }
      return fullPath
    } catch (error) {
      console.error('Error extracting S3 key from URL:', error)
      // Fallback to the old method
      return url.split('?')[0].split('/').pop()
    }
  }

  submitForm() {
    const form = this.element.closest('form')
    if (form) {
      debug('uppy-uploader', '📸📽️ Submitting form:', form)
      form.submit()
    } else {
      console.error('📸📽️ No form found to submit')
    }
  }
}
